import { useMemo, useState } from 'react';
import { App, Button, Card, Pagination, Popconfirm, Segmented, Select, Skeleton, Space, Timeline as AntTimeline, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ActivityItem, FieldDefinitionDto } from '@crm/shared';
import { useActivities, useAddNote, useDeleteNote } from '../features/activities/api';
import { useDeals } from '../features/deals/api';
import { errorMessage } from '../api/client';
import { ActivityText, activityIcon } from './activity-text';
import { EmptyState, LoadError } from './states';
import { MentionTextArea } from './Mentions';
import { fmtDateTime } from '../utils/format';
import { useFeatures } from '../features/settings/api';

const GROUPS: Record<string, string[] | undefined> = {
  All: undefined,
  Notes: ['NOTE', 'CALL', 'MEETING'],
  Emails: ['EMAIL_SENT', 'EMAIL_RECEIVED', 'EMAIL_DELIVERED', 'EMAIL_OPENED', 'EMAIL_CLICKED', 'EMAIL_BOUNCED', 'EMAIL_COMPLAINED', 'UNSUBSCRIBED'],
  Changes: ['FIELD_CHANGED', 'CONTACT_CREATED', 'ORGANIZATION_CHANGED', 'TAG_ADDED', 'TAG_REMOVED', 'LIST_ADDED', 'LIST_REMOVED', 'SEGMENT_ENTERED', 'SEGMENT_LEFT', 'MERGED', 'UNMERGED', 'IMPORTED', 'VERIFIED', 'FORM_SUBMITTED'],
  Tasks: ['TASK_CREATED', 'TASK_DONE'],
  Deals: ['DEAL_CREATED', 'STAGE_CHANGED', 'DEAL_WON', 'DEAL_LOST'],
};

interface Props {
  contactId?: string;
  organizationId?: string;
  dealId?: string;
  fields?: FieldDefinitionDto[];
  /** Show which contact each item belongs to (organization view). */
  showContact?: boolean;
}

export function Timeline({ contactId, organizationId, dealId, fields = [], showContact }: Props) {
  const { message } = App.useApp();
  const [group, setGroup] = useState('All');
  const [page, setPage] = useState(1);
  const [noteType, setNoteType] = useState<'NOTE' | 'CALL' | 'MEETING'>('NOTE');
  const [body, setBody] = useState('');
  /** on a contact, a note can be about one of the contact's deals instead of the contact in general */
  const [relatedDealId, setRelatedDealId] = useState<string | null>(null);
  const dealsOn = useFeatures().enabled('pipelines');
  const contactDeals = useDeals({ contactId, status: 'all', pageSize: 100, sortBy: 'created_at', sortDir: 'desc' }, !!contactId && !dealId && dealsOn);
  const dealOptions = (contactDeals.data?.items ?? []).map((d) => ({ value: d.id, label: d.pipeline ? `${d.title} · ${d.pipeline.name}` : d.title }));
  const activities = useActivities({ contactId, organizationId, dealId, types: GROUPS[group], page, pageSize: 25 });
  const addNote = useAddNote();
  const deleteNote = useDeleteNote();
  const labels = useMemo(() => ({ emails: 'Emails', phones: 'Phones', addresses: 'Addresses', main_contact: 'Main contact', ...Object.fromEntries(fields.map((f) => [f.key, f.label])) }), [fields]);

  const submit = async () => {
    if (!body.trim()) return;
    try {
      await addNote.mutateAsync({ type: noteType, body: body.trim(), contactId, organizationId, dealId: dealId ?? relatedDealId ?? undefined });
      setBody('');
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const items = (activities.data?.items ?? []).map((a: ActivityItem) => ({
    key: a.id,
    icon: activityIcon(a.type),
    content: (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <ActivityText a={a} labels={labels} showContact={showContact} showDeal={!dealId} />
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
            {fmtDateTime(a.occurredAt)}
          </Typography.Text>
        </div>
        {['NOTE', 'CALL', 'MEETING'].includes(a.type) && (
          <Popconfirm
            title="Delete this entry?"
            okText="Delete"
            okButtonProps={{ danger: true, loading: deleteNote.isPending }}
            onConfirm={() => deleteNote.mutateAsync(a.id).then(() => message.success('Entry deleted')).catch((e) => message.error(errorMessage(e)))}
          >
            <Button size="small" type="text" icon={<DeleteOutlined />} aria-label="Delete entry" title="Delete entry" />
          </Popconfirm>
        )}
      </div>
    ),
  }));

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space.Compact style={{ width: '100%' }}>
          <Select value={noteType} onChange={setNoteType} style={{ width: 120 }} options={[{ value: 'NOTE', label: 'Note' }, { value: 'CALL', label: 'Call' }, { value: 'MEETING', label: 'Meeting' }]} />
          <MentionTextArea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a note… (Cmd/Ctrl+Enter to save)" autoSize={{ minRows: 1, maxRows: 6 }} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void submit(); }} />
          <Button type="primary" onClick={() => void submit()} loading={addNote.isPending} disabled={!body.trim()}>
            Add
          </Button>
        </Space.Compact>
        {dealOptions.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography.Text type="secondary">Related to</Typography.Text>
            <Select
              size="small"
              aria-label="Note related to"
              style={{ minWidth: 240 }}
              value={relatedDealId ?? ''}
              onChange={(v) => setRelatedDealId(v || null)}
              options={[{ value: '', label: 'The contact in general' }, { label: 'Deal', title: 'Deal', options: dealOptions }]}
            />
          </div>
        )}
      </Card>
      <div style={{ marginBottom: 16 }}>
        <Segmented options={Object.keys(GROUPS)} value={group} onChange={(v) => { setGroup(String(v)); setPage(1); }} />
      </div>
      {activities.isError ? (
        <LoadError error={activities.error} onRetry={activities.refetch} title="Could not load the history" />
      ) : activities.isLoading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : activities.data && activities.data.items.length === 0 ? (
        <EmptyState description={group === 'All' ? 'No history yet. Notes, emails and changes appear here.' : `No ${group.toLowerCase()} yet.`} />
      ) : (
        <AntTimeline items={items} />
      )}
      {activities.data && activities.data.total > 25 && (
        <Pagination size="small" current={page} pageSize={25} total={activities.data.total} onChange={setPage} style={{ marginTop: 8 }} />
      )}
    </div>
  );
}
